import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import BaseTile from '../../../components/Tile/BaseTile';
import TileContent, { TileStatusContent } from '../../../components/Tile/TileContent';
import { useGlobalAPI } from "../../../hooks/useGlobalAPI";
import { getFileMetadata } from "../../../features/visualization/LeadTimeHistogram/api/leadTimeHistogramApi";
import TileSettingsModal from '../../../components/common/TileSettingsModal';
import LifecycleTimeKPISettings from './LifecycleTimeKPISettings';
import { useGraphData } from "../../../stores/GraphDataContext";
import { useAutoFitFontSize } from "../_shared/useAutoFitFontSize";

const LIFECYCLE_TIME_VALUE_MAX_PX = 60; // was text-6xl
const LIFECYCLE_TIME_VALUE_MIN_PX = 20;

// Extracted so useAutoFitFontSize is called from a real component's render, not from the nested
// renderKPICard() helper — hooks called from a non-component helper violate the Rules of Hooks.
// No compact fallback: this is a formatted duration string, not a raw large count.
const LifecycleTimeBigValue = ({ text }) => {
  const { ref, fontSizePx } = useAutoFitFontSize({
    text,
    maxPx: LIFECYCLE_TIME_VALUE_MAX_PX,
    minPx: LIFECYCLE_TIME_VALUE_MIN_PX,
  });

  return (
    <div ref={ref} className="max-w-full truncate font-bold mb-2 text-primary-500" style={{ fontSize: `${fontSizePx}px` }}>
      {text}
    </div>
  );
};

const LifecycleTimeKPI = ({ activeFile, onRefresh, onGetHeaderActions, onTitleChange }) => {
  const { t } = useTranslation();
  const api = useGlobalAPI(activeFile);
  const { filteredGraphData: graphData } = useGraphData();

  // State for KPI data and UI
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);

  // Form state
  const [objectType, setObjectType] = useState('');
  const [fromActivity, setFromActivity] = useState('');
  const [toActivity, setToActivity] = useState('');
  const [aggregationMethod, setAggregationMethod] = useState('median');
  const [kpiTitle, setKpiTitle] = useState('Time Between Activities');

  // Settings modal state
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);

  // Data for dropdowns
  const [objectTypes, setObjectTypes] = useState([]);
  const [objectTypesLoading, setObjectTypesLoading] = useState(false);
  const [activityTypes, setActivityTypes] = useState([]);
  const [objectTypeActivities, setObjectTypeActivities] = useState({});
  const [activitiesLoading, setActivitiesLoading] = useState(false);

  const handleOpenSettings = useCallback((e) => {
    e.stopPropagation(); // Prevent grid drag activation
    setIsSettingsModalOpen(true);
  }, []);

  const handleCloseSettings = useCallback(() => {
    setIsSettingsModalOpen(false);
  }, []);


  // Fetch object types
  useEffect(() => {
    const fetchObjectTypes = async () => {
      if (!activeFile) {
        setObjectTypes([]);
        return;
      }

      setObjectTypesLoading(true);
      try {
        console.log('Fetching object types for LifecycleTimeKPI:', activeFile);
        const metadata = await getFileMetadata(activeFile);
        if (metadata?.object_types) {
          const types = Object.keys(metadata.object_types).sort();
          setObjectTypes(types);
          console.log('Object types loaded for LifecycleTimeKPI:', types);
        } else {
          setObjectTypes(['Auftrag', 'Angebot', 'Lieferschein'].sort());
        }
      } catch (error) {
        console.error('Error fetching object types for LifecycleTimeKPI:', error);
        setObjectTypes(['Auftrag', 'Angebot', 'Lieferschein'].sort());
      } finally {
        setObjectTypesLoading(false);
      }
    };

    fetchObjectTypes();
  }, [activeFile]);

  // Cache for activities by file to prevent repeated network calls and dropdown blinking
  const activitiesCacheRef = React.useRef(new Map()); // key: activeFile, value: {objectTypeActivities, allActivities}

  // Fetch activities (per object type map) once per active file
  useEffect(() => {
    let cancelled = false;

    const loadFromCache = () => {
      const cacheKey = activeFile || 'default';
      const cached = activitiesCacheRef.current.get(cacheKey);
      if (cached) {
        setObjectTypeActivities(cached.objectTypeActivities || {});
        setActivityTypes(cached.allActivities || []);
        return true;
      }
      return false;
    };

    const fetchActivities = async () => {
      if (loadFromCache()) return; // nothing to do

      setActivitiesLoading(true);
      try {
        console.log('Fetching activities for LifecycleTimeKPI:', activeFile);
        // Use the object-type-activities endpoint (do NOT override file_type here; let useGlobalAPI set it)
        let response = await api.post('/object-type-activities', {}, {}, 'Object Type Activities');
        // Fallback to GET in case POST is blocked by some proxy or validation
        if (!response || !response.objectTypeActivities) {
          console.warn('POST /object-type-activities returned no data. Trying GET fallback...');
          response = await api.get('/object-type-activities', {}, {}, 'Object Type Activities (GET Fallback)');
        }
        if (cancelled) return;

        if (response && response.objectTypeActivities) {
          const mapping = response.objectTypeActivities || {};
          // Save full map for per-object-type filtering
          setObjectTypeActivities(mapping);
          // Get all activities across all object types
          const allActivities = new Set();
          Object.values(mapping).forEach(activities => {
            if (Array.isArray(activities)) {
              activities.forEach(activity => allActivities.add(activity));
            }
          });
          const activitiesList = Array.from(allActivities).sort();
          setActivityTypes(activitiesList);

          // Write into cache
          const cacheKey = activeFile || 'default';
          activitiesCacheRef.current.set(cacheKey, {
            objectTypeActivities: mapping,
            allActivities: activitiesList,
          });

          console.log('Activities loaded for LifecycleTimeKPI:', activitiesList.length);
        } else {
          setActivityTypes([]);
          setObjectTypeActivities({});
        }
      } catch (error) {
        if (cancelled) return;
        console.error('Error fetching activities for LifecycleTimeKPI:', error);
        setActivityTypes([]);
        setObjectTypeActivities({});
      } finally {
        if (!cancelled) setActivitiesLoading(false);
      }
    };

    // On file change, try cache first, else fetch
    setActivitiesLoading(true);
    const hadCache = loadFromCache();
    if (hadCache) {
      setActivitiesLoading(false);
    } else {
      fetchActivities();
    }

    return () => {
      cancelled = true;
    };
  }, [activeFile]);

  const fetchKPIData = useCallback(async () => {
    // Validate form before making request (title is optional)
    if (!objectType || !fromActivity || !toActivity) {
      // Don't set error on initial load - let user configure
      if (hasFetched) {
        setError("Please select object type and two different activities");
      }
      return;
    }

    if (fromActivity === toActivity) {
      setError("From and To activities must be different");
      return;
    }

    setLoading(true);
    setError(null);
    setHasFetched(true);

    try {
      console.log("Fetching lifecycle time KPI data...", {
        activeFile,
        objectType,
        fromActivity,
        toActivity,
        aggregationMethod,
        kpiTitle
      });

      // Filters are automatically merged by api (local or global)
      const payload = {
        // Do not set file_type here; useGlobalAPI will inject active file or default
        object_type: objectType,
        from_activity: fromActivity,
        to_activity: toActivity,
        aggregation_method: aggregationMethod,
        custom_title: kpiTitle,
      };
      const response = await api.post('/kpi/lifecycle-time', payload, {}, 'Lifecycle Time KPI');

      console.log(" Lifecycle Time KPI response received:", response);
      setData(response);
    } catch (err) {
      console.error(" Error during KPI data fetch:", err);
      // Extract useful error details from FastAPI/axios style errors
      let message = "An unknown error occurred.";
      try {
        const detail = err?.response?.data?.detail ?? err?.data?.detail ?? err?.detail;
        if (Array.isArray(detail)) {
          // Pydantic validation error format
          message = detail.map(d => d.msg || JSON.stringify(d)).join("; ");
        } else if (typeof detail === 'string') {
          message = detail;
        } else if (detail && typeof detail === 'object') {
          message = JSON.stringify(detail);
        } else if (err?.message) {
          message = err.message;
        }
      } catch (_) {
        message = err?.message || message;
      }
      setError(`Failed to calculate Lifecycle Time KPI: ${message}`);
    } finally {
      setLoading(false);
    }
  }, [activeFile, objectType, fromActivity, toActivity, aggregationMethod, kpiTitle, api]);

  const hasData = !loading && !error && data !== null && (data.occurrences ?? 0) > 0;
  const showInitialButton = !hasFetched && !loading;

  // Handle apply settings - defined after fetchKPIData
  const handleApplySettings = useCallback(() => {
    // Update tile title if changed
    if (onTitleChange && kpiTitle) {
      onTitleChange(kpiTitle);
    }

    setIsSettingsModalOpen(false);
    // Trigger KPI calculation with current settings
    fetchKPIData();
  }, [fetchKPIData, onTitleChange, kpiTitle]);

  // Header actions - settings button
  const headerActions = useMemo(() => (
    <button
      onClick={handleOpenSettings}
      className="flex items-center gap-1 px-2 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
      aria-label={t('common:actions.openSettings')}
    >
      <svg
        className="w-3 h-3"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
        />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
        />
      </svg>
      <span className="text-xs">{t('common:actions.settings')}</span>
    </button>
  ), [handleOpenSettings]);

  // Compute activities available for current object type once and reuse
  const availableActivities = useMemo(() => {
    if (objectType && objectTypeActivities && Array.isArray(objectTypeActivities[objectType])) {
      return [...objectTypeActivities[objectType]].sort();
    }
    // Fallback: show all activities if specific mapping missing
    return objectType ? activityTypes : [];
  }, [objectType, objectTypeActivities, activityTypes]);

  // Heuristic ordering guidance using OCDFG adjacent edges.
  // Note: Lifecycle KPI allows non-adjacent B after A, but if even adjacency A->B
  // is never observed and reverse B->A exists, warn the user that selection likely
  // yields no matches.
  const { orderingHint, hasReverseNoForward } = useMemo(() => {
    try {
      if (!graphData?.edges_performance?.event_couples || !objectType || !fromActivity || !toActivity) {
        return { orderingHint: '', hasReverseNoForward: false };
      }
      const objectEdges = graphData.edges_performance.event_couples[objectType];
      if (!objectEdges) return { orderingHint: '', hasReverseNoForward: false };

      const forwardKey = `${fromActivity},${toActivity}`;
      const reverseKey = `${toActivity},${fromActivity}`;
      const hasForward = Object.prototype.hasOwnProperty.call(objectEdges, forwardKey);
      const hasReverse = Object.prototype.hasOwnProperty.call(objectEdges, reverseKey);

      if (!hasForward && hasReverse) {
        return {
          orderingHint:
            `Warning: In this dataset, "${toActivity}" frequently occurs before "${fromActivity}". ` +
            `Calculation is disabled for this pair to avoid reverse sequence results.`,
          hasReverseNoForward: true
        };
      }
      return { orderingHint: '', hasReverseNoForward: false };
    } catch (e) {
      return { orderingHint: '', hasReverseNoForward: false };
    }
  }, [graphData, objectType, fromActivity, toActivity]);

  const isFormValid = () => {
    // Allow interaction if we already have activities for the selected object type,
    // even if background loading is still true
    const activitiesReady = !activitiesLoading || (objectType && availableActivities.length > 0);

    const valid = Boolean(objectType &&
           fromActivity &&
           toActivity &&
           fromActivity !== toActivity &&
           activitiesReady);

    console.log('🔍 Form validation:', {
      objectType,
      fromActivity,
      toActivity,
      activitiesLoading,
      availableActivities: availableActivities.length,
      activitiesReady,
      valid,
      applyDisabled: !activeFile || !valid || loading
    });

    return valid;
  };

  // Handle Enter key press on form inputs - update title
  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      // Update the tile title
      if (onTitleChange && kpiTitle) {
        onTitleChange(kpiTitle);
      }
    }
  }, [kpiTitle, onTitleChange]);

  // Disabled auto-load - user must click "Calculate KPI" button
  // useAutoLoad(fetchKPIData, [activeFile, api.currentFilters, activity, dateAttribute1, dateAttribute2, condition, aggregation, kpiTitle], hasData || !isFormValid());

  // Provide refresh function to parent (GridComponent)
  useEffect(() => {
    if (onRefresh) {
      onRefresh(fetchKPIData);
    }
  }, [onRefresh, fetchKPIData]);

  // Provide header actions to parent (GridComponent)
  useEffect(() => {
    if (onGetHeaderActions) {
      onGetHeaderActions(headerActions);
    }
  }, [onGetHeaderActions, headerActions]);

  const formatTimeValue = (seconds) => {
    if (seconds == null || isNaN(seconds)) return '—';

    const total = Math.max(0, Math.floor(seconds));
    const d = Math.floor(total / 86400);
    const h = Math.floor((total % 86400) / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = Math.floor(total % 60);

    const parts = [];
    if (d) parts.push(`${d}d`);
    if (h) parts.push(`${h}h`);
    if (m) parts.push(`${m}m`);
    if (s && parts.length === 0) parts.push(`${s}s`); // show seconds only if no larger unit present

    // Return up to two largest non-zero units (e.g., 2d 3h, 1h 5m)
    return parts.slice(0, 2).join(' ') || '0s';
  };

  const renderKPICard = () => {
    if (!data) return null;

    const aggregationLabel = {
      'median': 'Median',
      'average': 'Average',
      'minimum': 'Minimum',
      'maximum': 'Maximum'
    }[data.aggregation_method] || data.aggregation_method;

    return (
      <div className="flex flex-col gap-4 w-full">
        {/* KPI Title - Display custom title */}
        {data.custom_title && data.custom_title !== 'Time Between Activities' && (
          <div className="text-center">
            <h3 className="text-xl font-bold text-gray-800">{data.custom_title}</h3>
          </div>
        )}

        <div className="rounded-md p-4 flex-1 flex flex-col items-center justify-center text-center min-h-[120px]">
          <LifecycleTimeBigValue
            text={data.aggregated_time_seconds !== null ? formatTimeValue(data.aggregated_time_seconds) : '—'}
          />
          <div className="text-lg font-medium text-gray-700 mb-1">
            {aggregationLabel} Time
          </div>
          <div className="text-sm text-gray-500 text-center max-w-md">
            {data.from_activity} → {data.to_activity}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 text-center">
          <div className="bg-blue-50 rounded-md p-3">
            <div className="text-2xl font-bold text-blue-700">
              {data.occurrences?.toLocaleString('de-DE') || '0'}
            </div>
            <div className="text-xs text-blue-600">Occurrences</div>
          </div>
          <div className="bg-green-50 rounded-md p-3">
            <div className="text-2xl font-bold text-green-700">
              {data.object_type}
            </div>
            <div className="text-xs text-green-600">Object Type</div>
          </div>
        </div>
      </div>
    );
  };

  console.log('🎨 LifecycleTimeKPI render - showInitialButton:', showInitialButton, {
    objectType,
    fromActivity,
    toActivity,
    kpiTitle,
    activitiesLoading,
    activeFile,
    isFormValid: isFormValid(),
    applyDisabled: !isFormValid() || loading
  });

  return (
    <BaseTile
      loading={loading}
      error={error}
      minHeight="auto"
      padding="sm"
      className=""
    >
      <TileContent layout="flex" direction="column" grow>
        {/* Initial Load Button */}
        {showInitialButton && (
          <div className="flex items-center justify-center h-full">
            <button
              onClick={handleOpenSettings}
              disabled={loading}
              className="px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-md hover:bg-primary-600 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            >
              Configure KPI
            </button>
          </div>
        )}

        {/* KPI Results */}
        {hasData && (
          <div className="pt-2">
            {renderKPICard()}
          </div>
        )}

        {/* No Data Message */}
        {hasFetched && !loading && !error && !hasData && (
          <TileStatusContent
            status="empty"
            title="No Data Found"
            message="No matching sequences found for the selected activities. Try adjusting your filters or activity selections."
            action={
              <button
                onClick={fetchKPIData}
                className="px-4 py-2 bg-gray-500 text-white text-sm font-medium rounded-md hover:bg-gray-600 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              >
                Retry
              </button>
            }
          />
        )}

        {/* Error State with Retry */}
        {error && hasFetched && (
          <div className="flex justify-center mt-4">
            <button
              onClick={fetchKPIData}
              className="px-4 py-2 bg-gray-500 text-white text-sm font-medium rounded-md hover:bg-gray-600 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
              Retry
            </button>
          </div>
        )}
      </TileContent>

      {/* Settings Modal */}
      <TileSettingsModal
        isOpen={isSettingsModalOpen}
        onClose={handleCloseSettings}
        onApply={handleApplySettings}
        title="Time Between Activities KPI Settings"
        isApplyDisabled={!isFormValid() || loading || hasReverseNoForward}
        applyButtonText={hasReverseNoForward ? "Pair Not Allowed" : (loading ? "Calculating..." : hasFetched ? "Update KPI" : "Calculate KPI")}
      >
        <LifecycleTimeKPISettings
          kpiTitle={kpiTitle}
          onKpiTitleChange={setKpiTitle}
          objectType={objectType}
          onObjectTypeChange={(type) => { setObjectType(type); setFromActivity(''); setToActivity(''); }}
          fromActivity={fromActivity}
          onFromActivityChange={setFromActivity}
          toActivity={toActivity}
          onToActivityChange={setToActivity}
          aggregationMethod={aggregationMethod}
          onAggregationMethodChange={setAggregationMethod}
          objectTypes={objectTypes}
          activities={availableActivities}
          loading={loading}
          objectTypesLoading={objectTypesLoading}
          activitiesLoading={activitiesLoading}
          onKeyPress={handleKeyPress}
          activeFile={activeFile}
          orderingHint={orderingHint}
        />
      </TileSettingsModal>
    </BaseTile>
  );
};

export default LifecycleTimeKPI;
